import { useRef } from 'react';
import { isBlurEvent } from '@makemydeal/dr-shared-ui-utils';

export type UserTypedGuard = {
    /** Call from onChange. Ignores Interstate's post-blur re-fire, which carries the blur event. */
    markTyped: (e: any) => void;
    /** Runs the callback only if a keystroke preceded this commit, then disarms until the next keystroke. */
    recordIfUserEdited: () => void;
};

/**
 * Tracks whether a field's pending value came from the user typing rather than from code writing the same
 * value. Fields that build their own onChange/onBlur handlers need this; fields that take handlers from
 * usePaymentCall get the equivalent through its onUserEdit option.
 *
 * ResidualField deliberately keeps its own version: it gates on a blur-in-progress ref that also suppresses
 * the value write, and it disarms without recording when the field is cleared.
 */
export const useUserTypedGuard = (onUserEdit: () => void): UserTypedGuard => {
    const userTyped = useRef(false);

    return {
        markTyped: (e: any) => {
            if (!isBlurEvent(e)) userTyped.current = true;
        },
        recordIfUserEdited: () => {
            if (!userTyped.current) return;
            userTyped.current = false;
            onUserEdit();
        }
    };
};
